// Numbers behind the Trips dashboard. Pure functions over the pipeline rows so
// they are easy to test; the page only draws what comes back.
// Spec: docs/admin/trip-pipeline.md

import { STAGES, stageIndex, type Journey, type Stage } from './tripPipeline';

export type Period = '30' | '90' | 'all';

const DAY = 86_400_000;

/** Journeys created within the period (relative to `now`). Abandoned and exited rows are dropped: they aren't part of the live pipeline. */
export function inPeriod(all: Journey[], period: Period, now = new Date()): Journey[] {
  const cutoff = period === 'all' ? -Infinity : now.getTime() - Number(period) * DAY;
  return all.filter((j) => j.stage !== 'abandoned' && j.stage !== 'exited' && !j.archived && new Date(j.created_at).getTime() >= cutoff);
}

export interface Tiles {
  total: number;
  active: number;
  needsAttention: number;
  completed: number;
  surveyRate: number | null; // answered / completed-or-later
}

export function tiles(rows: Journey[]): Tiles {
  const active = rows.filter((j) => stageIndex(j.stage) >= 0 && stageIndex(j.stage) <= 3);
  const done = rows.filter((j) => stageIndex(j.stage) >= 4);
  const answered = rows.filter((j) => j.stage === 'survey_answered');
  return {
    total: rows.length,
    active: active.length,
    needsAttention: rows.filter((j) => j.flags.length > 0).length,
    completed: done.length,
    surveyRate: done.length ? answered.length / done.length : null,
  };
}

/** How many journeys reached each stage (or passed it), for the funnel. */
export function funnel(rows: Journey[]): { stage: Stage; count: number }[] {
  return STAGES.map((s, i) => ({
    stage: s,
    count: rows.filter((j) => {
      if (stageIndex(j.stage) >= i) return true;
      if (i === 1) return !!j.milestones.paid;
      if (i === 2) return !!j.milestones.host_confirmed;
      return false;
    }).length,
  }));
}

/** Monday (UTC) of the week containing the date, as YYYY-MM-DD. */
export function weekStart(iso: string): string {
  const d = new Date(iso);
  const day = (d.getUTCDay() + 6) % 7;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day)).toISOString().slice(0, 10);
}

/** The last `n` week starts ending with this week, oldest first. */
export function lastWeeks(n: number, now = new Date()): string[] {
  const end = new Date(weekStart(now.toISOString()) + 'T00:00:00Z').getTime();
  return Array.from({ length: n }, (_, i) => new Date(end - (n - 1 - i) * 7 * DAY).toISOString().slice(0, 10));
}

/** Per week created: journeys by the stage they are in now. */
export function stagePerWeek(rows: Journey[], weeks: string[]): { week: string; byStage: Record<string, number> }[] {
  return weeks.map((week) => {
    const byStage: Record<string, number> = {};
    for (const j of rows) {
      if (weekStart(j.created_at) !== week) continue;
      byStage[j.stage] = (byStage[j.stage] ?? 0) + 1;
    }
    return { week, byStage };
  });
}

/** Created per week next to trips that ended per week. */
export function createdVsCompleted(rows: Journey[], weeks: string[]): { week: string; created: number; completed: number }[] {
  return weeks.map((week) => ({
    week,
    created: rows.filter((j) => weekStart(j.created_at) === week).length,
    completed: rows.filter((j) => {
      const end = j.milestones.marked_completed ?? j.end_date;
      return stageIndex(j.stage) >= 4 && !!end && weekStart(end) === week;
    }).length,
  }));
}

const gap = (a?: string, b?: string): number | null => {
  if (!a || !b) return null;
  const d = (new Date(b).getTime() - new Date(a).getTime()) / DAY;
  return d >= 0 ? d : null;
};

/** Average days between milestones, over the journeys that have both. */
export function avgDaysPerStage(rows: Journey[]): { label: string; days: number | null; n: number }[] {
  const steps: { label: string; from: string; to: string }[] = [
    { label: 'Chat → paid', from: 'chat_started', to: 'paid' },
    { label: 'Paid → host confirmed', from: 'paid', to: 'host_confirmed' },
    { label: 'Confirmed → trip starts', from: 'host_confirmed', to: 'trip_starts' },
    { label: 'Trip ends → survey answered', from: 'trip_ends', to: 'survey_answered' },
  ];
  return steps.map((s) => {
    const vals = rows.map((j) => gap(j.milestones[s.from], j.milestones[s.to])).filter((v): v is number => v !== null);
    return { label: s.label, n: vals.length, days: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null };
  });
}

/** Journeys that have waited longest in a live stage, flagged ones first on ties. */
export function topStalled(rows: Journey[], limit = 10): Journey[] {
  return rows
    .filter((j) => stageIndex(j.stage) >= 0 && stageIndex(j.stage) <= 3)
    .sort((a, b) => b.days_in_stage - a.days_in_stage || b.flags.length - a.flags.length)
    .slice(0, limit);
}
